(() => {
  'use strict';

  const ARTICLE_SELECTOR = '[data-finch-article]';
  const GLOBAL_KEY = '__finchArticleLayout';

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].initializeWithin(document);
    return;
  }

  const initializedArticles = new WeakSet();
  const articleCleanups = new WeakMap();

  const getArticleRoots = (root) => {
    const articles = [];

    if (root instanceof Element && root.matches(ARTICLE_SELECTOR)) {
      articles.push(root);
    }

    root.querySelectorAll?.(ARTICLE_SELECTOR).forEach((article) => articles.push(article));
    return articles;
  };

  const isVisible = (element) => {
    if (element.hidden || element.closest('[hidden], [aria-hidden="true"]')) return false;

    const styles = window.getComputedStyle(element);
    return styles.display !== 'none' && styles.visibility !== 'hidden' && element.getClientRects().length > 0;
  };

  const slugify = (value, fallback) => {
    const slug = value
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, '-en-')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return slug || fallback;
  };

  const assignHeadingIds = (headings) => {
    const idCounts = new Map();
    const usedIds = new Set();

    document.querySelectorAll('[id]').forEach((element) => {
      const id = element.id.trim();
      if (!id) return;

      usedIds.add(id);
      idCounts.set(id, (idCounts.get(id) || 0) + 1);
    });

    headings.forEach((heading, index) => {
      const existingId = heading.id.trim();
      if (existingId && idCounts.get(existingId) === 1) return;

      const baseId = slugify(heading.textContent.trim(), `hoofdstuk-${index + 1}`);
      let candidate = baseId;
      let suffix = 2;

      while (usedIds.has(candidate)) {
        candidate = `${baseId}-${suffix}`;
        suffix += 1;
      }

      heading.id = candidate;
      usedIds.add(candidate);
    });
  };

  const setupTableOfContents = (article) => {
    const content = article.querySelector('[data-article-content]');
    const toc = article.querySelector('[data-article-toc]');
    const list = article.querySelector('[data-article-toc-list]');

    if (!content || !toc || !list) return () => {};

    const headings = Array.from(content.querySelectorAll('h2, h3')).filter(isVisible);
    list.replaceChildren();

    if (headings.length === 0) {
      toc.hidden = true;
      return () => {};
    }

    toc.hidden = false;
    assignHeadingIds(headings);

    const links = headings.map((heading) => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      const level = heading.tagName === 'H3' ? '3' : '2';

      item.className = 'finch-article__toc-item';
      item.dataset.headingLevel = level;
      link.className = 'finch-article__toc-link';
      link.dataset.articleTocLink = '';
      link.dataset.headingLevel = level;
      link.href = `#${encodeURIComponent(heading.id)}`;
      link.textContent = heading.textContent.trim();

      item.append(link);
      list.append(item);

      return link;
    });

    const setActiveLink = (headingId) => {
      links.forEach((link) => {
        const isActive = decodeURIComponent(link.hash.slice(1)) === headingId;

        if (isActive) {
          link.setAttribute('aria-current', 'location');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    };

    setActiveLink(headings[0].id);

    const linkController = new AbortController();
    links.forEach((link, index) => {
      link.addEventListener('click', () => setActiveLink(headings[index].id), {
        signal: linkController.signal,
      });
    });

    if (!('IntersectionObserver' in window)) {
      return () => linkController.abort();
    }

    const visibleHeadings = new Set();
    const headingOrder = new Map(headings.map((heading, index) => [heading, index]));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visibleHeadings.add(entry.target);
          } else {
            visibleHeadings.delete(entry.target);
          }
        });

        if (visibleHeadings.size === 0) return;

        const activeHeading = Array.from(visibleHeadings).sort(
          (first, second) => headingOrder.get(first) - headingOrder.get(second)
        )[0];

        setActiveLink(activeHeading.id);
      },
      {
        rootMargin: '-15% 0px -70% 0px',
        threshold: 0,
      }
    );

    headings.forEach((heading) => observer.observe(heading));

    return () => {
      observer.disconnect();
      linkController.abort();
    };
  };

  const fallbackCopy = (value) => {
    const input = document.createElement('textarea');
    const focusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const selection = document.getSelection();
    const selectedRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

    input.value = value;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.inset = '0 auto auto -9999px';
    input.style.opacity = '0';
    document.body.append(input);
    input.select();
    input.setSelectionRange(0, input.value.length);

    let copied = false;
    try {
      copied = document.execCommand('copy');
    } finally {
      input.remove();
      if (selection && selectedRange) {
        selection.removeAllRanges();
        selection.addRange(selectedRange);
      }
      focusedElement?.focus({ preventScroll: true });
    }

    if (!copied) throw new Error('Copy command was not accepted');
  };

  const copyText = async (value) => {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(value);
        return;
      } catch (error) {
        // Browser permissions can reject the modern API; use the legacy fallback below.
      }
    }

    fallbackCopy(value);
  };

  const setupCopyButtons = (article) => {
    const buttons = Array.from(article.querySelectorAll('[data-article-copy]'));
    if (buttons.length === 0) return () => {};

    const controller = new AbortController();
    const resetTimers = new Set();
    const statusTimers = new WeakMap();
    const canonicalUrl = document.querySelector('link[rel="canonical"]')?.href;

    buttons.forEach((button) => {
      const status = article.querySelector('[data-article-copy-status]');
      const initialStatus = status?.textContent || '';
      const initiallyHidden = status?.hidden ?? false;

      button.addEventListener(
        'click',
        async () => {
          const explicitUrl = button.dataset.copyUrl || article.dataset.articleUrl || button.dataset.articleCopy;
          const shareUrl = explicitUrl || canonicalUrl || `${window.location.origin}${window.location.pathname}${window.location.search}`;
          const successMessage =
            button.dataset.copySuccess || article.dataset.copySuccess || status?.dataset.copySuccess || 'Link gekopieerd';
          const errorMessage =
            button.dataset.copyError || article.dataset.copyError || status?.dataset.copyError || 'Kopiëren is niet gelukt';

          let message = successMessage;
          try {
            await copyText(shareUrl);
          } catch (error) {
            message = errorMessage;
          }

          if (!status) return;

          const previousTimer = statusTimers.get(status);
          if (previousTimer) {
            window.clearTimeout(previousTimer);
            resetTimers.delete(previousTimer);
          }

          status.setAttribute('role', 'status');
          status.setAttribute('aria-live', 'polite');
          status.textContent = message;
          status.hidden = false;

          const timer = window.setTimeout(() => {
            status.textContent = initialStatus;
            status.hidden = initiallyHidden;
            resetTimers.delete(timer);
            statusTimers.delete(status);
          }, 2000);

          resetTimers.add(timer);
          statusTimers.set(status, timer);
        },
        { signal: controller.signal }
      );
    });

    return () => {
      controller.abort();
      resetTimers.forEach((timer) => window.clearTimeout(timer));
      resetTimers.clear();
    };
  };

  const initializeArticle = (article) => {
    if (initializedArticles.has(article) || article.dataset.finchArticleInitialized === 'true') return;

    initializedArticles.add(article);
    article.dataset.finchArticleInitialized = 'true';

    const cleanups = [setupTableOfContents(article), setupCopyButtons(article)];
    articleCleanups.set(article, () => cleanups.forEach((cleanup) => cleanup()));
  };

  const initializeWithin = (root = document) => {
    getArticleRoots(root).forEach(initializeArticle);
  };

  const cleanupWithin = (root) => {
    getArticleRoots(root).forEach((article) => {
      articleCleanups.get(article)?.();
      articleCleanups.delete(article);
    });
  };

  window[GLOBAL_KEY] = { initializeWithin, cleanupWithin };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeWithin(document), { once: true });
  } else {
    initializeWithin(document);
  }

  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanupWithin(event.target));
})();
